import { useParams } from 'react-router-dom';
import { useTicker } from './ticker/useTicker.js';
import './TickerPage.css';

/* Opponent case encodes the venue: uppercase home, lowercase away. */
const isHome = (opp) => opp === opp.toUpperCase();

function Fixture({ fixture }) {
	if (!fixture.opp) {
		return (
			<span className="fixture fixture--blank" title="Blank gameweek">
				—
			</span>
		);
	}
	const home = isHome(fixture.opp);
	return (
		<span
			className="fixture"
			data-difficulty={fixture.fdr}
			title={`${fixture.opp.toUpperCase()} (${home ? 'home' : 'away'}) · difficulty ${fixture.fdr}`}
		>
			{fixture.opp.toUpperCase()}
			<span className="fixture__venue">{home ? 'H' : 'A'}</span>
		</span>
	);
}

export default function TickerPage() {
	const { user } = useParams();
	const ticker = useTicker(user || 'nutmeg');
	const { status, error, rows, headers } = ticker;

	return (
		<div className="ticker-page">
			<header className="ticker-page__intro">
				<h1>Fixture ticker</h1>
				<p className="ticker-page__lede">
					Upcoming fixtures by team, coloured by difficulty. Easiest run first.
				</p>
			</header>

			{status === 'error' && (
				<div className="notice notice--error" role="alert">
					<strong>Could not load the ticker.</strong>
					<span>{error}</span>
				</div>
			)}

			{status === 'loading' && (
				<div className="ticker-page__loading" role="status">
					<span className="spinner" aria-hidden="true" />
					<span>Loading fixtures…</span>
				</div>
			)}

			{status === 'ready' && (
				<>
					<div className="ticker-controls">
						<div className="ticker-controls__group">
							<span className="ticker-controls__label">From</span>
							<button
								className="btn btn--secondary btn--icon"
								type="button"
								onClick={() => ticker.adjust('start', 1)}
								disabled={!ticker.canGrowStart}
								aria-label="Show an earlier gameweek"
							>
								−
							</button>
							<span className="ticker-controls__value numeric">
								GW{ticker.start + 1}
							</span>
							<button
								className="btn btn--secondary btn--icon"
								type="button"
								onClick={() => ticker.adjust('start', -1)}
								disabled={!ticker.canShrink}
								aria-label="Drop the earliest gameweek"
							>
								+
							</button>
						</div>

						<div className="ticker-controls__group">
							<span className="ticker-controls__label">To</span>
							<button
								className="btn btn--secondary btn--icon"
								type="button"
								onClick={() => ticker.adjust('end', -1)}
								disabled={!ticker.canShrink}
								aria-label="Drop the latest gameweek"
							>
								−
							</button>
							<span className="ticker-controls__value numeric">GW{ticker.end}</span>
							<button
								className="btn btn--secondary btn--icon"
								type="button"
								onClick={() => ticker.adjust('end', 1)}
								disabled={!ticker.canGrowEnd}
								aria-label="Show a later gameweek"
							>
								+
							</button>
						</div>

						<label className="ticker-controls__toggle">
							<input
								type="checkbox"
								checked={ticker.sortByDifficulty}
								onChange={(e) => ticker.setSortByDifficulty(e.target.checked)}
							/>
							Sort by easiest run
						</label>
					</div>

					<div className="scroll-x">
						<table className="ticker-table">
							<thead>
								<tr>
									<th scope="col" className="ticker-table__team">
										Team
									</th>
									{headers.map((h) => (
										<th key={h} scope="col">
											{h}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{rows.map((row) => (
									<tr key={row.team}>
										<th scope="row" className="ticker-table__team">
											{row.team}
										</th>
										{row.gameweeks.map(([gw, fixtures]) => (
											<td key={gw}>
												<div className="fixture-stack">
													{fixtures.length ? (
														fixtures.map((f, i) => <Fixture key={i} fixture={f} />)
													) : (
														<Fixture fixture={{ opp: '' }} />
													)}
												</div>
											</td>
										))}
									</tr>
								))}
							</tbody>
						</table>
					</div>

					<p className="ticker-page__legend">
						<span className="legend-swatch" data-difficulty="1" /> Easiest
						<span className="legend-swatch" data-difficulty="2" />
						<span className="legend-swatch" data-difficulty="3" />
						<span className="legend-swatch" data-difficulty="4" />
						<span className="legend-swatch" data-difficulty="5" /> Hardest
						<span className="ticker-page__legend-note">
							Uppercase code with H is a home fixture, A away. — is a blank.
						</span>
					</p>
				</>
			)}
		</div>
	);
}
